import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import InfoTooltipSvg from '@/components/common/InfoTooltipSvg';
import { Cookie } from '@/tscapi/utils/cookie';

import { Dialog } from './Dialog';
import { useAuthenticated } from './hooks/useAuthenticated';
import { useCart, useRemoveItem, useUpdateCart } from './hooks/useCart';
import { useCartData } from './hooks/useCartData';
import { useOrder } from './hooks/useOrder';
import { en, fr } from './i18n';
import { ItemCard } from './ItemCard';
import { ItemCardModify } from './ItemCardModify';
import { UpsellItemCard } from './UpsellItemCard';

type ItemProps = {
    isModifyOrder: boolean;
    isBagEmpty: boolean;
};

// Constants
const SELLER_TYPES = {
    TSC: 'TSC',
    TSC_PARTNER: 'TSCPartner',
    MARKETPLACE: 'Marketplace',
} as const;

function Items({ isModifyOrder, isBagEmpty }: ItemProps) {
    // Cookie instance for reading user data
    const cookie = new Cookie();

    // State
    const [showRemoveDialog, setShowRemoveDialog] = useState(false);
    const [currentItemId, setCurrentItemId] = useState(0);

    // Translations
    const { i18n, t } = useTranslation('shoppingbag');

    // React Query hooks
    const isAuthenticated = useAuthenticated();
    const { data } = useCart(isAuthenticated);
    const { isPickup } = useCartData(data);
    const order = useOrder(isAuthenticated, cookie.ModifyOrderNumber);
    const mutation = useRemoveItem(data);
    const updateMutation = useUpdateCart(cookie?.UserAccessToken?.Edp, cookie?.CartGuid);

    // Memoized remove handler
    const handleRemoveClick = (itemId: number) => {
        setCurrentItemId(itemId);
        setShowRemoveDialog(true);
    };

    // Memoized dialog close handler
    const handleDialogClose = () => {
        setShowRemoveDialog(false);
    };

    // Memoized confirm remove handler
    const handleConfirmRemove = () => {
        mutation.mutate(currentItemId);
        setShowRemoveDialog(false);
    };

    // Find current item for dialog
    const currentItem = useMemo(
        () => data?.CartLines?.find((item) => item.Id === currentItemId),
        [data?.CartLines, currentItemId],
    );

    // Initialize translation bundles only once
    useEffect(() => {
        i18n.addResourceBundle('en', 'shoppingbag', en, true, true);
        i18n.addResourceBundle('fr', 'shoppingbag', fr, true, true);
    }, [i18n]);

    // Memoized filtered and counted data
    const { tscItems, tscPartnerItems, marketplaceItems, marketplaceSellers } = useMemo(() => {
        if (!data?.CartLines || !data?.Products) {
            return {
                tscItems: [],
                tscPartnerItems: [],
                marketplaceItems: [],
                marketplaceSellers: [],
            };
        }

        const nonDonationItems = data.CartLines.filter((item) => !item.CartLineItem.IsDonation);

        const tsc = nonDonationItems.filter((item) => {
            const product = data.Products.find((x) => x.ItemNo === item.CartLineItem.ItemNo);
            return product?.SellerType === SELLER_TYPES.TSC;
        });

        const tscPartner = nonDonationItems.filter((item) => {
            const product = data.Products.find((x) => x.ItemNo === item.CartLineItem.ItemNo);
            return product?.SellerType === SELLER_TYPES.TSC_PARTNER;
        });

        const marketplace = nonDonationItems.filter((item) => {
            const product = data.Products.find((x) => x.ItemNo === item.CartLineItem.ItemNo);
            return product?.SellerType === SELLER_TYPES.MARKETPLACE;
        });

        const sellers = [
            ...new Set(
                data.Products.filter((product) => product.SellerType === SELLER_TYPES.MARKETPLACE)
                    .map((product) => product.SellerName)
                    .filter(Boolean),
            ),
        ].sort();

        return {
            tscItems: tsc,
            tscPartnerItems: tscPartner,
            marketplaceItems: marketplace,
            marketplaceSellers: sellers,
        };
    }, [data?.CartLines, data?.Products]);

    // Early return for no data
    if (!data && !order.data) {
        return null;
    }

    return (
        <>
            {isModifyOrder && !isBagEmpty && (
                <div className="bag__note">
                    <InfoTooltipSvg className="bag__note-svg" />
                    {t("Adding an item here doesn't hold it. So get what you love before it's gone.")}
                </div>
            )}

            {tscItems.length > 0 && (
                <>
                    {!isModifyOrder && <div className="bag__fulfilled--title">{t('Sold and fulfilled by TSC')}</div>}
                    <div className={tscItems.length > 0 ? 'bag__itemlist noBorder' : 'bag__itemlist'}>
                        {tscItems.map((item) => (
                            <ItemCard
                                containerCss="item__container"
                                CartLine={item}
                                CartProducts={data.Products}
                                isModifyOrder={isModifyOrder}
                                CartMessages={data.CartMessages}
                                IsPickup={isPickup}
                                key={item.Id}
                                Update={updateMutation}
                                Remove={() => handleRemoveClick(item.Id)}
                                Promocode={data.PromoCode}
                            />
                        ))}
                    </div>
                </>
            )}

            {tscPartnerItems.length > 0 && (
                <>
                    {!isModifyOrder && (
                        <div className="bag__fulfilled--title">{t('Sold by TSC, fulfilled by TSC Partner')}</div>
                    )}
                    <div className={tscPartnerItems.length > 0 ? 'bag__itemlist noBorder' : 'bag__itemlist'}>
                        {tscPartnerItems.map((item) => (
                            <ItemCard
                                containerCss="item__container"
                                CartLine={item}
                                isModifyOrder={isModifyOrder}
                                CartProducts={data.Products}
                                CartMessages={data.CartMessages}
                                IsPickup={isPickup}
                                key={item.Id}
                                Update={updateMutation}
                                Remove={() => handleRemoveClick(item.Id)}
                                Promocode={data.PromoCode}
                            />
                        ))}
                    </div>
                </>
            )}

            {marketplaceItems.length > 0 && (
                <>
                    {marketplaceSellers.map((seller, index) => (
                        <React.Fragment key={seller}>
                            {!isModifyOrder && (
                                <div className="bag__fulfilled--title">{t('Sold and fulfilled by') + ' ' + seller}</div>
                            )}
                            <div
                                className={
                                    index !== marketplaceSellers.length - 1 ? 'bag__itemlist noBorder' : 'bag__itemlist'
                                }
                            >
                                {marketplaceItems
                                    .filter((item) => {
                                        const product = data.Products.find(
                                            (x) => x.ItemNo === item.CartLineItem.ItemNo,
                                        );
                                        return product?.SellerName === seller;
                                    })
                                    .map((item) => (
                                        <ItemCard
                                            containerCss="item__container"
                                            CartLine={item}
                                            isModifyOrder={isModifyOrder}
                                            CartProducts={data.Products}
                                            CartMessages={data.CartMessages}
                                            IsPickup={isPickup}
                                            key={item.Id}
                                            Update={updateMutation}
                                            Remove={() => handleRemoveClick(item.Id)}
                                            Promocode={data.PromoCode}
                                        />
                                    ))}
                            </div>
                        </React.Fragment>
                    ))}
                </>
            )}

            {data.UpsellProducts?.length > 0 && (
                <UpsellItemCard UpsellProducts={data.UpsellProducts} Update={updateMutation} />
            )}

            {isModifyOrder && order?.data && (
                <div className="bag__itemlist">
                    {order.data.ShipLevels[0].Items.map((item, index) => (
                        <ItemCardModify containerCss="item__container" ShipmentItem={item} key={'modify' + index} />
                    ))}
                </div>
            )}

            {showRemoveDialog && currentItem && (
                <Dialog
                    title={t('Are you sure you want to remove this item from your bag?')}
                    containerCss="removeDialog"
                    height="auto"
                    onCloseClick={handleDialogClose}
                >
                    <ItemCard
                        containerCss="item__container"
                        CartLine={currentItem}
                        CartProducts={data.Products}
                        CartMessages={data.CartMessages}
                        IsPickup={isPickup}
                        isModifyOrder={isModifyOrder}
                        key={'dialog' + currentItem.Id}
                        Update={updateMutation}
                        hideQty={true}
                        Remove={() => {
                            mutation.mutate(currentItem.Id);
                        }}
                        Promocode={data.PromoCode}
                    />
                    <div className="dialog__buttons--container">
                        <button
                            type="button"
                            aria-label={t('Remove item from bag')}
                            className="dialog__buttons remove"
                            onClick={handleConfirmRemove}
                        >
                            {t('Remove')}
                        </button>
                        <button
                            type="button"
                            aria-label={t('Cancel remove item from bag')}
                            className="dialog__buttons cancel"
                            onClick={handleDialogClose}
                        >
                            {t('Cancel')}
                        </button>
                    </div>
                </Dialog>
            )}
        </>
    );
}

export default Items;
